import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Scale, Swords, Shield, Gavel, Sparkles, Loader2, Lock, RotateCcw, Send, Share2, History, MoveRight, TrendingUp, ArrowRight, Mail } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { bumpToolStat } from "@/lib/toolStats";
import { playGavel, unlockAudio } from "@/lib/sound";
import { AiThinking } from "@/components/AiThinking";
import { CinematicVideo } from "@/components/CinematicVideo";
import { DialecticCore } from "@/components/DialecticCore";

const WaIcon = ({ size = 15 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor"><path d="M12.04 2c-5.46 0-9.9 4.44-9.9 9.9 0 1.75.46 3.45 1.32 4.95L2 22l5.28-1.38a9.87 9.87 0 004.76 1.21h.004c5.46 0 9.9-4.44 9.9-9.9 0-2.64-1.03-5.13-2.9-7A9.82 9.82 0 0012.04 2zm5.8 14.06c-.24.68-1.42 1.32-1.95 1.36-.5.05-1.12.24-3.66-.77-3.08-1.22-5.03-4.38-5.18-4.58-.15-.2-1.24-1.65-1.24-3.15s.79-2.24 1.07-2.55c.28-.3.6-.38.8-.38.2 0 .4.002.57.01.18.008.43-.07.67.51.24.6.82 2.07.9 2.22.07.15.12.32.02.52-.1.2-.15.32-.3.5-.15.17-.31.38-.44.51-.15.15-.3.31-.13.6.17.3.76 1.25 1.63 2.02 1.12 1 2.06 1.31 2.36 1.46.3.15.47.12.64-.07.17-.2.74-.86.94-1.16.2-.3.4-.25.67-.15.28.1 1.75.83 2.05.98.3.15.5.22.57.35.07.13.07.72-.17 1.4z"/></svg>
);

const MAX = 600;
const PROCES_VIDEO = "https://customer-assets-7cd3h4nn.emergentagent.net/job_epiphany-mentor/artifacts/eib6e99b_gemini_generated_video_d4cfc4e2.mp4";

const CARD_T = {
  fr: { card: "Carte à partager", error: "Le partage a échoué.", wa: "WhatsApp", email: "E-mail" },
  en: { card: "Shareable card", error: "Sharing failed.", wa: "WhatsApp", email: "Email" },
  es: { card: "Tarjeta para compartir", error: "Falló al compartir.", wa: "WhatsApp", email: "Correo" },
  nl: { card: "Deelbare kaart", error: "Delen mislukt.", wa: "WhatsApp", email: "E-mail" },
};

function scoreTone(score) {
  if (score >= 70) return { text: "text-emerald-300", ring: "hsl(152 60% 50%)", bg: "bg-emerald-400/10 border-emerald-400/30" };
  if (score >= 40) return { text: "text-primary", ring: "hsl(32 90% 55%)", bg: "bg-primary/10 border-primary/30" };
  return { text: "text-red-300", ring: "hsl(0 72% 60%)", bg: "bg-red-400/10 border-red-400/30" };
}

export default function ProcesIdees({ onExplore }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [idea, setIdea] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [locked, setLocked] = useState(false);
  const [history, setHistory] = useState([]);
  const [prevScore, setPrevScore] = useState(null);

  const pj = t("atelier.pj", { returnObjects: true }) || {};
  const presets = Array.isArray(pj.presets) ? pj.presets : [];
  const lng = (i18n.language || "fr").slice(0, 2);
  const ct = CARD_T[lng] || CARD_T.fr;
  const [sharing, setSharing] = useState(false);

  const loadHistory = () => {
    api.get("/proces/history").then(({ data }) => setHistory(data.items || [])).catch(() => {});
  };
  useEffect(() => { loadHistory(); }, []);
  useEffect(() => {
    const pending = localStorage.getItem("coccon_pending_proces");
    if (pending) { localStorage.removeItem("coccon_pending_proces"); run(pending); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const run = async (value, opts = {}) => {
    const q = ((value ?? idea) || "").trim();
    if (!q || loading) return;
    unlockAudio();
    setIdea(q);
    setLoading(true);
    setData(null);
    setLocked(false);
    setPrevScore(opts.prevScore ?? null);
    try {
      const { data: res } = await api.post("/proces/judge", { idea: q, lang: (i18n.language || "fr").slice(0, 2), fresh: !!opts.fresh });
      setData(res);
      bumpToolStat("proces");
      playGavel();
      loadHistory();
    } catch (e) {
      if (e.response?.status === 402) setLocked(true);
      else toast.error(formatApiErrorDetail(e.response?.data?.detail) || pj.error);
    } finally {
      setLoading(false);
    }
  };

  const reset = () => { setData(null); setIdea(""); setPrevScore(null); };
  const reformulate = (value) => { setData(null); setLocked(false); setPrevScore(null); setIdea(value || ""); };
  const deepen = () => {
    if (!data || !onExplore) return;
    onExplore(t("atelier.pj.deepenPrompt", { idea: data.idea, score: data.score }));
  };

  const fetchCardBlob = async () => {
    const { data: blob } = await api.post("/proces/card", {
      idea: data.idea, score: data.score, verdict_label: data.verdict_label,
      lang: lng, eyebrow: pj.title, score_label: pj.scoreLabel,
    }, { responseType: "blob" });
    return blob;
  };

  const shareSummary = () =>
    `« ${data.idea} » — ${pj.title} : ${data.score}/100${data.verdict_label ? `, ${data.verdict_label}` : ""}.\n${window.location.origin}/atelier`;

  const shareCard = async () => {
    if (!data || sharing) return;
    setSharing(true);
    try {
      const blob = await fetchCardBlob();
      const file = new File([blob], "cocoon-proces.png", { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: pj.title });
      } else {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = "cocoon-proces.png"; a.click();
        URL.revokeObjectURL(url);
      }
    } catch (e) {
      if (e?.name !== "AbortError") toast.error(ct.error);
    } finally {
      setSharing(false);
    }
  };

  const shareWhatsApp = async () => {
    if (!data || sharing) return;
    setSharing(true);
    const caption = shareSummary();
    const waFallback = () => window.open(`https://wa.me/?text=${encodeURIComponent(caption)}`, "_blank", "noopener,noreferrer");
    try {
      const blob = await fetchCardBlob();
      const file = new File([blob], "cocoon-proces.png", { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], text: caption, title: pj.title });
      } else {
        waFallback();
      }
    } catch (e) {
      if (e?.name !== "AbortError") waFallback();
    } finally {
      setSharing(false);
    }
  };

  const shareEmail = () => {
    if (!data) return;
    const subject = `${pj.title} — ${data.score}/100`;
    window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(shareSummary())}`;
  };

  const cards = data ? [
    { key: "prosecutor", label: pj.prosecutor, Icon: Swords, cls: "border-red-400/30 bg-red-400/[0.06]", name: "text-red-300", chip: "bg-red-400/15 text-red-300" },
    { key: "advocate", label: pj.advocate, Icon: Shield, cls: "border-emerald-300/25 bg-emerald-300/[0.05]", name: "text-emerald-300", chip: "bg-emerald-300/12 text-emerald-300" },
    { key: "verdict", label: pj.judge, Icon: Gavel, cls: "border-primary/25 bg-primary/[0.06]", name: "text-primary", chip: "bg-primary/15 text-primary" },
  ] : [];
  const tone = data ? scoreTone(data.score) : null;

  return (
    <div className="h-full overflow-y-auto scroll-thin">
      <div className="max-w-3xl mx-auto px-5 sm:px-6 py-10 sm:py-14">
        <div className="text-center mb-8">
          <span className="inline-flex items-center gap-2 text-[0.7rem] uppercase tracking-[0.3em] text-primary/70 mb-3">
            <Scale size={14} /> {pj.eyebrow}
          </span>
          <h1 className="font-display text-3xl sm:text-4xl text-gradient-orange leading-tight mb-3" data-testid="pj-title">{pj.title}</h1>
          <p className="text-foreground/60 leading-relaxed max-w-xl mx-auto text-sm sm:text-base">{pj.subtitle}</p>
        </div>

        {/* cinématique en fondu */}
        <div className="mb-8">
          <CinematicVideo fade sound src={PROCES_VIDEO} testId="proces-cinematic-video" caption={pj.subtitle} />
        </div>

        <form onSubmit={(e) => { e.preventDefault(); run(); }} className="rounded-2xl bg-white/[0.03] border border-white/12 p-3 mb-3" data-testid="pj-form">
          <textarea
            value={idea}
            onChange={(e) => setIdea(e.target.value.slice(0, MAX))}
            placeholder={pj.placeholder}
            rows={3}
            data-testid="pj-input"
            className="w-full bg-transparent px-3 py-2 text-sm sm:text-base text-foreground placeholder:text-foreground/35 focus:outline-none resize-none"
          />
          <div className="flex items-center justify-between px-2 pt-1">
            <span className="text-xs text-foreground/35">{idea.length}/{MAX}</span>
            <button type="submit" disabled={loading || !idea.trim()} data-testid="pj-submit"
              className="inline-flex items-center gap-2 rounded-full bg-primary/15 border border-primary/40 text-primary px-5 py-2.5 text-sm font-medium hover:bg-primary/25 transition-colors disabled:opacity-50">
              {loading ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} />} {pj.go}
            </button>
          </div>
        </form>

        {!data && !loading && !locked && (
          <div className="mb-6" data-testid="pj-presets">
            <p className="text-xs text-foreground/40 mb-2.5">{pj.suggest}</p>
            <div className="flex flex-wrap gap-2">
              {presets.map((p, i) => (
                <button key={i} onClick={() => run(p)} data-testid={`pj-preset-${i}`}
                  className="text-xs sm:text-sm px-3.5 py-2 rounded-full border border-primary/20 text-foreground/70 hover:text-primary hover:bg-primary/[0.06] hover:border-primary/40 transition-colors">
                  {p}
                </button>
              ))}
            </div>

            {history.length > 0 && (
              <div className="mt-6" data-testid="pj-history">
                <p className="text-xs text-foreground/40 mb-2.5 flex items-center gap-1.5"><History size={13} /> {pj.history}</p>
                <div className="flex flex-wrap gap-2">
                  {history.map((h, i) => {
                    const tone = scoreTone(h.score || 0);
                    return (
                      <div key={i} data-testid={`pj-history-item-${i}`}
                        className="flex items-center gap-1 rounded-full border border-white/10 pl-3.5 pr-1 py-1 text-xs sm:text-sm max-w-full">
                        <button onClick={() => reformulate(h.idea)} data-testid={`pj-history-reformulate-${i}`} title={pj.reformulate}
                          className="truncate max-w-[200px] text-foreground/60 hover:text-primary transition-colors">
                          {h.idea}
                        </button>
                        <span className={`flex-shrink-0 tabular-nums font-semibold px-2 py-0.5 rounded-full text-[0.7rem] border ${tone.bg} ${tone.text}`}>{h.score}</span>
                        <button onClick={() => run(h.idea, { fresh: true, prevScore: h.score })} data-testid={`pj-history-rejuger-${i}`} title={pj.rejuger}
                          className="flex-shrink-0 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[0.7rem] font-semibold text-primary/90 border border-primary/30 hover:bg-primary/10 transition-colors">
                          <RotateCcw size={12} /> {pj.rejuger}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {locked && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
            className="glass rounded-2xl border border-primary/25 p-8 text-center frame-pulse" data-testid="pj-locked">
            <span className="w-14 h-14 mx-auto rounded-2xl bg-primary/10 flex items-center justify-center text-primary mb-4"><Lock size={24} strokeWidth={1.6} /></span>
            <p className="font-display text-xl text-foreground/90 mb-2">{pj.locked}</p>
            <p className="text-sm text-foreground/55 leading-relaxed max-w-md mx-auto mb-6">{pj.lockedDesc}</p>
            <button onClick={() => navigate("/tarifs")} data-testid="pj-locked-cta"
              className="btn-frost-orange inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm">
              <Sparkles size={15} /> {pj.lockedCta}
            </button>
          </motion.div>
        )}

        {loading && <AiThinking variant="proces" skeleton="cards" testId="pj-loading" />}

        <AnimatePresence>
          {data && !loading && (
            <motion.div key={data.idea} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
              className="space-y-5" data-testid="pj-result">
              <div className="text-center">
                <p className="uppercase tracking-[0.25em] text-[0.7rem] text-primary/70 mb-1.5">{pj.ideaLabel}</p>
                <p className="font-display italic text-lg sm:text-xl text-foreground/85 leading-snug">{data.idea}</p>
              </div>

              <div className={`glass rounded-2xl border p-6 text-center stats-wow ${tone.bg}`} data-testid="pj-score">
                <p className="uppercase tracking-[0.25em] text-[0.7rem] text-foreground/55 mb-1">{pj.scoreLabel}</p>
                <p className={`font-display leading-none ${tone.text}`}><span className="text-6xl sm:text-7xl">{data.score}</span><span className="text-2xl text-foreground/40">/100</span></p>
                {prevScore != null && (
                  (() => {
                    const delta = data.score - prevScore;
                    const up = delta > 0, flat = delta === 0;
                    const dc = flat ? "text-foreground/50" : up ? "text-emerald-300" : "text-red-300";
                    return (
                      <p className="mt-2.5 flex items-center justify-center gap-2 text-sm" data-testid="pj-score-evolution">
                        <span className="uppercase tracking-[0.18em] text-[0.62rem] text-foreground/45">{pj.evolution}</span>
                        <span className="tabular-nums text-foreground/45 line-through">{prevScore}</span>
                        <MoveRight size={14} className="text-foreground/35" />
                        <span className={`tabular-nums font-semibold ${dc}`}>{data.score}</span>
                        {!flat && <span className={`tabular-nums font-semibold text-[0.72rem] px-2 py-0.5 rounded-full border ${up ? "text-emerald-300 border-emerald-300/30 bg-emerald-300/10" : "text-red-300 border-red-300/30 bg-red-300/10"}`}>{up ? "+" : ""}{delta}</span>}
                      </p>
                    );
                  })()
                )}
                {prevScore != null && data.score > prevScore && (
                  <p className="mt-3 inline-flex items-center gap-2 text-xs sm:text-sm text-emerald-300 border border-emerald-300/30 bg-emerald-300/10 rounded-full px-4 py-2" data-testid="pj-score-celebrate">
                    <TrendingUp size={14} /> {pj.celebrate}
                  </p>
                )}
                {prevScore != null && data.score < prevScore && (
                  <button onClick={() => reformulate(data.idea)} data-testid="pj-score-encourage"
                    className="mt-3 inline-flex items-center gap-2 text-xs sm:text-sm text-primary/90 border border-primary/30 rounded-full px-4 py-2 hover:bg-primary/10 transition-colors">
                    <Sparkles size={14} /> {pj.encourage}
                  </button>
                )}
                {data.verdict_label && <p className={`mt-2 font-display ${tone.text}`}>{data.verdict_label}</p>}
                <DialecticCore variant="scale" tilt={(data.score - 50) / 50} testId="pj-core" />
                {Array.isArray(data.history_scores) && data.history_scores.length >= 2 && (
                  <div className="mt-4 max-w-[260px] mx-auto" data-testid="pj-score-graph">
                    <svg viewBox="0 0 240 48" className="w-full h-12" role="img" aria-label="score evolution">
                      {(() => {
                        const arr = data.history_scores;
                        const n = arr.length, W = 240, H = 48, P = 8;
                        const cx = (i) => P + (n <= 1 ? 0 : (i / (n - 1)) * (W - 2 * P));
                        const cy = (s) => (H - P) - (Math.max(0, Math.min(100, s)) / 100) * (H - 2 * P);
                        const pts = arr.map((s, i) => `${cx(i)},${cy(s)}`).join(" ");
                        return (
                          <>
                            <polyline points={pts} fill="none" stroke="hsl(32 90% 55%)" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
                            {arr.map((s, i) => (
                              <g key={i}>
                                <circle cx={cx(i)} cy={cy(s)} r={i === n - 1 ? 4 : 3} fill={i === n - 1 ? "hsl(32 95% 62%)" : "hsl(32 55% 42%)"} />
                                <text x={cx(i)} y={cy(s) - 8} textAnchor="middle" fontSize="10" fill="hsl(214 12% 62%)" className="tabular-nums">{s}</text>
                              </g>
                            ))}
                          </>
                        );
                      })()}
                    </svg>
                  </div>
                )}
              </div>

              <div className="grid gap-3 sm:grid-cols-3">
                {cards.map((c) => (
                  <div key={c.key} className={`rounded-2xl border p-4 ${c.cls}`} data-testid={`pj-${c.key}`}>
                    <p className={`font-display flex items-center gap-2 mb-2 ${c.name}`}>
                      <span className={`w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 ${c.chip}`}>
                        {c.key === "verdict" ? (
                          <motion.span style={{ display: "inline-flex", transformOrigin: "70% 30%" }}
                            initial={{ rotate: -34 }} animate={{ rotate: [-34, 8, -14, 0] }}
                            transition={{ duration: 0.55, times: [0, 0.4, 0.7, 1], ease: "easeOut", delay: 0.15 }}>
                            <c.Icon size={15} />
                          </motion.span>
                        ) : (
                          <c.Icon size={15} />
                        )}
                      </span>
                      {c.label}
                    </p>
                    <p className="text-sm text-foreground/75 leading-relaxed">{data[c.key]}</p>
                  </div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-1">
                <button onClick={deepen} data-testid="pj-deepen" className="btn-frost-orange inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm">
                  <Sparkles size={15} /> {pj.deepen}
                </button>
                <button onClick={shareCard} disabled={sharing} data-testid="pj-share" className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm text-primary/90 border border-primary/30 hover:bg-primary/10 transition-colors disabled:opacity-50">
                  {sharing ? <Loader2 size={15} className="animate-spin" /> : <Share2 size={15} />} {ct.card}
                </button>
                <button onClick={shareWhatsApp} disabled={sharing} data-testid="pj-share-wa" className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm text-[#25D366] border border-[#25D366]/30 hover:bg-[#25D366]/10 transition-colors disabled:opacity-50">
                  {sharing ? <Loader2 size={15} className="animate-spin" /> : <WaIcon />} {ct.wa}
                </button>
                <button onClick={shareEmail} data-testid="pj-share-email" className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm text-foreground/70 hover:text-primary border border-white/10 hover:border-primary/30 transition-colors">
                  <Mail size={15} /> {ct.email}
                </button>
                <button onClick={reset} data-testid="pj-again" className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm text-foreground/60 hover:text-primary border border-white/10 hover:border-primary/30 transition-colors">
                  <RotateCcw size={15} /> {pj.again}
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
